import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { IFile } from '../domain/file-entity';
import { IClaim } from 'src/app/shared/domain/claim.entity';
import { ClaimQuoteRequest } from '../domain/requests/claim-quote-request';
import { ICryptoRequest } from '../domain/requests/crypto.request.entity';
import { IRequestId } from '../domain/id.entity';

@Injectable()
export class ClaimsService {

  private readonly baseUrl = `${environment.apiBaseUrl}claims`;
  constructor(private httpClient: HttpClient) {
  }

  query(body: IClaimsSearch): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/search', body);
  }

  save(body: ClaimQuoteRequest): Observable<number> {
    return this.httpClient.post<number>(this.baseUrl + '/save', body);
  }

  get(body: IRequestId): Observable<IClaim> {
    return this.httpClient.post<IClaim>(this.baseUrl + '', body);
  }

  getFiles(body: IRequestId): Observable<IFile[]> {
    return this.httpClient.post<IFile[]>(this.baseUrl + '/files', body);
  }

  saveAttachment(body: ISaveAttachmentRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/save-files', body);
  }

  verify(body: IVerifyRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/verify', body);
  }

  verifyThirdParty(body: IVerifyThirdPartyRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/verifythirdparty', body);
  }

  insertThirdPartyClaim(body: IInsertThirdPartyClaimRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/insertthirdpartyclaim', body);
  }

  cancel(body: ICancelRequest): Observable<void> {
    return this.httpClient.post<void>(this.baseUrl + '/cancel', body);
  }

  resubmit(body: IResubmitClaimRequest): Observable<any> {
    return this.httpClient.post<any>(this.baseUrl + '/resubmit', body);
  }

  getManualParts(body: IRequestId): Observable<IManualOrder[]> {
    return this.httpClient.post<IManualOrder[]>(this.baseUrl + '/manual-parts', body);
  }

  getTraxparts(body: IRequestId): Observable<ITraxOrder[]> {
    return this.httpClient.post<ITraxOrder[]>(this.baseUrl + '/trax-parts', body);
  }
}

export interface IResubmitClaimRequest extends ICryptoRequest {
  id: number;
  dealerId: number;
  contactId: number;
  resubmitNotes: string;
  deletedImageIds: string;
  removedClaimDetailIds: string;
  newClaimFiles: string;
}

export interface IClaimsSearch extends ICryptoRequest {
  claimType: number;
  manId?: number;
  contactId?: number;
  searchText: string;
  status: number;
  isApprovalDate?: boolean;
  fromDate?: Date;
  toDate?: Date;
  dateType?: string;
  orderBy: string;
  orderHow: number;
  pageSize: number;
  pageNumber: number;
}

export interface ICancelRequest extends ICryptoRequest {
  id: number;
  reason: number;
  notes: string;
}

export class IBaseEstimatePart {
  partNo: string;
  shopName: string;
  shopId: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  partQty: number;
  mtcPartId: number;
  mtc: number;
  nissan_certified: number;
  floorSalePrice?: number;
  discountTypeCode: number;
  roNum: string;
  invoiceNum: string;
  matchedQuantity: number;
  removedFromMtc?: boolean;
  lineNo?: number;
  salePrice?: number;

  // front end prop
  matchKey?: number;
  isMatched: boolean;
  originalQty?: number;
}

export interface IManualOrder extends IBaseEstimatePart {
  partDescription: string;
  listPrice: number;
  rebate: number;
  shopConfirm: number;
  mtcClaimDetailId: number;
  createDate: Date;
}

export interface ITraxOrder extends IBaseEstimatePart {
  vMakecode: string;
  vModel: string;
  vModelYr: string;
  vVin: string;
  lineDesc: string;
  partProgramDescription?: string;
  actPrice?: number;
  partType: string;
  mtcList: number;
  mtcCost: number;
  mtcDiscount: number;
  origPartType: string;
  emsDetailId: number;
  rebateAmount: number;
  requestDate: Date;
  salePrice?: number;
}

/**
 * One invoice-part-to-claim-part match. invoiceItemId (ship ticket item_id) and
 * claimItemId (emsDetailId, or mtcClaimDetailId for manual claims) travel together
 * as a single tuple so the pairing can never be desynchronized by sorting/filtering.
 */
export interface IPartMatch {
  invoiceItemId: number;
  claimItemId: number;
  quantity: number;
  rebate: number;
}

export interface IVerifyRequest extends ICryptoRequest {
  id: number;
  isManual: boolean;
  manufacturerId: number;
  partMatches: IPartMatch[];
  estimateParts: IBaseEstimatePart[];
}

export interface IInsertThirdPartyClaimRequest extends ICryptoRequest {
  dealerId?: number;
  ticketId?: number;
  invoiceItemIds: string;
  invoiceQuantities: string;
  userId?: number;
  manufacturerId?: number;
  vin: string;
  claimRefNum: string;
  emsMakeId?: number;
  totalRebate?: number;
}

export interface ISaveAttachmentRequest extends ICryptoRequest {
  id: number;
  attachments: string[];
}

export interface IVerifyThirdPartyRequest extends ICryptoRequest {
  dealerId: number;
  manId: number;
  claimNum: string;
}
